import { Component, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import AOS from 'aos';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-techgeek-5',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-5.component.html',
  styleUrl: './techgeek-5.component.scss'
})
export class Techgeek5Component implements AfterViewInit, OnInit, OnDestroy {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 5";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit() {
    AOS.init();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

injectSchemaMarkup(): void {
    const techGeek5Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-5",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-5",
          "name": "Tech Geek Ed. 5 — Kalpita Technologies Tech Magazine",
          "description": "Kalpita Tech Geek Magazine Edition 5 covers 6G network rollout, blockchain in marketing across multiple industries, OMI AI wearable, and intelligent automation trends.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": ["https://heyzine.com/flip-book/b976798d45.html"],
          "datePublished": "2025-01-01",
          "dateModified": "2025-03-31",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-5#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 5", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-5" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 5: 6G, Blockchain Marketing, and AI Companions",
          "articleBody": "Kalpita Tech Geek Magazine Edition 5 explores 6G Network Rollout (1 Tbps speeds), Blockchain in Marketing (fraud-proof attribution), AI Wearables (OMI companion), and UiPath Intelligent Automation.",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/b976798d45.html",
            "name": "Tech Geek Ed. 5 — Digital Flipbook Edition"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Leverage Technology Trends from Tech Geek Ed. 5",
          "step": [
            { "@type": "HowToStep", "name": "6G Infrastructure Prep", "text": "Prepare for terahertz communication and speeds up to 1 Tbps by 2030." },
            { "@type": "HowToStep", "name": "Apply Blockchain Marketing", "text": "Implement immutable audit trails to ensure GDPR/CCPA compliance in marketing campaigns." },
            { "@type": "HowToStep", "name": "Evaluate AI Companions", "text": "Consider multimodal wearables like OMI AI for proactive memory recall and live observation." },
            { "@type": "HowToStep", "name": "Automate Enterprise Workflows", "text": "Deploy UiPath AI Center for training ML models directly into existing RPA tasks." },
            { "@type": "HowToStep", "name": "Optimize Aviation Ops", "text": "Use AI route optimization to save up to 4% fuel per flight by analyzing real-time air traffic data." },
            { "@type": "HowToStep", "name": "Decentralized Communication", "text": "Evaluate Bitchat for serverless, phone-number-free offline mesh messaging during emergencies." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the speed of 6G?",
              "acceptedAnswer": { "@type": "Answer", "text": "6G is expected to be up to 100 times faster than 5G, offering data rates of up to 1 Tbps with near-zero latency." }
            },
            {
              "@type": "Question",
              "name": "How does Bitchat enable offline messaging?",
              "acceptedAnswer": { "@type": "Answer", "text": "It uses Bluetooth Low Energy mesh networking to relay encrypted messages across nodes without requiring servers or internet." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek5Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }
  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  //   articles: ArticlePost[] = [
  //   {
  //     id: 1,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
  //   },
  //   {
  //     id: 2,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
  //   },
  //   {
  //     id: 3,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
  //   },
  //   {
  //     id: 4,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
  //   },
  //   {
  //     id: 5,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
  //   },
  //   {
  //     id: 6,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
  //   },
  //   {
  //     id: 7,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
  //   },
  //   {
  //     id: 8,
  //     title: 'Lorem ipsum dolor sit amet, consectetur elit',
  //     image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
  //   }
  // ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
